import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  ButtonWithSpinner,
  lineItemHelpers,
  LineItemSelector,
  Loading,
  PolicyRuleAlerts,
  policyRuleDisplayMessagePlacements,
  ProductDetailDrawer,
  ToastContext,
} from '@reveni-io/reveni-ui'

import ConsentModal from '../components/ConsentModal/ConsentModal'
import ExchangeNotAllowedModal from '../components/ExchangeNotAllowedModal/ExchangeNotAllowedModal'
import NoDeliveryMethodsAvailableModal from '../components/NoDeliveryMethodsAvailableModal/NoDeliveryMethodsAvailableModal'
import PolicyRuleMessageWrapper from '../components/PolicyRuleMessageWrapper/PolicyRuleMessageWrapper'
import RegionsModal from '../components/RegionsModal/RegionsModal'
import Summary from '../components/Summary/Summary'
import AppContext from '../context/AppContext'
import { loadingMessages } from '../DeliveryMethodsPage/constants'
import { getCustomDomain } from '../helpers/customDomain/customDomain'
import { sendEvent } from '../helpers/mixpanel'
import useAvailableTransactionTypesForRegion from '../hooks/useAvailableTransactionTypesForRegion/useAvailableTransactionTypesForRegion'
import useCancellationsPortal from '../hooks/useCancellationsPortal/useCancellationsPortal'
import useConsentModal from '../hooks/useConsentModal/useConsentModal'
import useCreateReturn from '../hooks/useCreateReturn/useCreateReturn'
import useDeliveryMethods from '../hooks/useDeliveryMethods/useDeliveryMethods'
import useItemTransactionType from '../hooks/useItemTransactionType/useItemTransactionType'
import useRegionModal from '../hooks/useRegionModal/useRegionModal'
import useReturnLabels from '../hooks/useReturnLabels/useReturnLabels'
import useReturnPreview from '../hooks/useReturnPreview/useReturnPreview'
import routes from '../Router/routes.constants'
import services from '../services/services'
import useHandleCreateReturnErrors from './hooks/useHandleCreateReturnErrors/useHandleCreateReturnErrors'
import useProductDetailDrawer from './hooks/useProductDetailDrawer/useProductDetailDrawer'
import ProductsSelectionMessage from './ProductsSelectionMessage/ProductsSelectionMessage'
import ReturnShippedErrorModal from './ReturnShippedErrorModal/ReturnShippedErrorModal'
import TransactionTypesNotAllowedModal from './TransactionTypesNotAllowedModal/TransactionTypesNotAllowedModal'
import { getExtraProducts, getNewRequestedProduct } from './helpers'
import * as Styles from './styles'

const ProductsSelectionPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { context, setContext } = useContext(AppContext)
  const { getDeliveryMethods, loading, addDeliveryMethodAndGoToNextPage, addingDeliveryMethod } = useDeliveryMethods()
  const [showNoDeliveryMethodsAvailableModal, setShowNoDeliveryMethodsAvailableModal] = useState(false)
  const { createReturn, loading: creatingReturn } = useCreateReturn()
  const { loading: loadingPreview } = useReturnPreview()
  const { setToastInfo } = useContext(ToastContext)
  const { showRegionModal, setShowRegionModal, shouldShowRegionModal, getProvinces, currentCountry, onChangeRegion } =
    useRegionModal()
  const { isItemReturnable, getItemNoReturnableReason, getItemDefaultTransactionType, showTransactionTypeSelector } =
    useItemTransactionType()
  const {
    handleErrors,
    showExchangeNotAllowedModal,
    closeExchangeNotAllowedModal,
    showReturnShippedErrorModal,
    closeReturnShippedErrorModal,
  } = useHandleCreateReturnErrors({ setContext })
  const { resetNumberOfReturnLabels } = useReturnLabels()
  const { isCancellationsPortal } = useCancellationsPortal()
  const { consentModal, showConsentModal, askForConsent, confirmConsent, closeConsentModal } = useConsentModal(
    policyRuleDisplayMessagePlacements.productsPage
  )
  const { showTransactionTypesNotAllowedModal, closeTransactionTypesNotAllowedModal, getTransactionsNotAllowedText } =
    useAvailableTransactionTypesForRegion({
      allowsUnifiedFlow: context?.merchantInfo?.preferences?.allows_unified_flow,
      availableTransactionTypes: context?.orderInfo?.available_transaction_types,
      availableTransactionTypesForRegion: context?.orderInfo?.available_transaction_types_for_region,
      autoOpenModal: true,
    })
  const { openProductDetailDrawer, closeProductDetailDrawer, showProductDetailDrawer, selectedProduct } =
    useProductDetailDrawer()
  const {
    getActionType,
    getCustomerImageUrl,
    getCustomerNotes,
    getReasonDisplay,
    getSubReasonDisplay,
    isItemSelected,
  } = lineItemHelpers(context.items)

  const onErrorAddingDeliveryMethod = error => {
    setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
  }

  const processReturn = async () => {
    const items = context.items
    try {
      const data = await createReturn(items)

      setContext(context => ({
        ...context,
        orderInfo: {
          ...context.orderInfo,
          customer_iban: data?.customer_iban,
          return_group: data?.return_group,
          fee_messages: data?.fee_messages,
        },
        allowed_region: data?.allowed_region,
      }))

      if (isCancellationsPortal && data?.delivery_method_id) {
        addDeliveryMethodAndGoToNextPage({
          returnId: data.id,
          payload: { delivery_method_id: data.delivery_method_id },
          isExchangeOrMixed: false,
          needsExchangeAddress: false,
          onError: onErrorAddingDeliveryMethod,
        })
      } else {
        if (shouldShowRegionModal(data?.allowed_region?.provinces)) {
          setShowRegionModal(true)
        } else {
          resetNumberOfReturnLabels()
          getDeliveryMethodsAvailableAndGoToDeliveryMethodsPage(context.orderInfo.shipping_address?.province, data.id)
        }
      }
    } catch (error) {
      handleErrors(error)
    }
  }

  const handleContinue = async () => {
    if (askForConsent()) return

    if (context?.items?.length > 0) {
      await processReturn()
    }
  }

  const getDeliveryMethodsAvailableAndGoToDeliveryMethodsPage = async (province, returnId) => {
    try {
      const data = await getDeliveryMethods({
        orderId: context.orderInfo.id,
        returnId: returnId,
        regionId: province?.id || context?.orderInfo?.shipping_address?.country?.region_id,
      })

      setContext(context => ({ ...context, deliveryMethods: data }))
      navigate(routes.deliveryMethodsPage)
    } catch {
      setShowNoDeliveryMethodsAvailableModal(true)
    }
  }

  const handleNoDeliveryMethodsAvailable = () => {
    const customDomain = getCustomDomain()

    if (customDomain) {
      navigate('/')
    } else {
      navigate(`/${context.merchantInfo.slug}`)
    }
  }

  const changeRegion = provinceId => {
    onChangeRegion(provinceId, newProvince => {
      getDeliveryMethodsAvailableAndGoToDeliveryMethodsPage(newProvince, context.orderInfo.returnId)
    })
  }

  const onClickInItem = item => {
    openProductDetailDrawer(item)
  }

  const handleCloseShippedErrorModal = () => {
    closeReturnShippedErrorModal(processReturn)
  }

  return (
    <Loading show={addingDeliveryMethod} messages={loadingMessages} height={335}>
      <PolicyRuleMessageWrapper>
        <PolicyRuleAlerts
          displayMessages={context.displayMessages}
          placement={policyRuleDisplayMessagePlacements.productsPage}
        />
      </PolicyRuleMessageWrapper>
      <ProductsSelectionMessage selectedItems={context.items} />
      <Styles.LineItemsContainer>
        {context?.orderInfo?.line_items?.map((item, index) => (
          <LineItemSelector
            key={index}
            lineItem={{
              ...item,
              actionType: getActionType(item.id),
              reason_display: getReasonDisplay(item.id),
              subreason_display: getSubReasonDisplay(item.id),
              customer_notes: getCustomerNotes(item.id),
              customer_image_url: getCustomerImageUrl(item.id),
            }}
            merchantInfo={context.merchantInfo}
            onClick={() => onClickInItem(item)}
            newItem={getNewRequestedProduct(context.items, item)}
            extraProducts={getExtraProducts(context.items, item)}
            isReturnable={isItemReturnable(item)}
            notReturnableReason={getItemNoReturnableReason(item)}
            selected={isItemSelected(item.id)}
            index={index + 1}
            clickable={isItemReturnable(item)}
          />
        ))}
        <Summary isCollapsable={context?.items?.length > 0} />
        <ButtonWithSpinner
          fullWidth
          defaultLabel={t('Continuar')}
          loadingLabel={t('Procesando')}
          loading={loading || creatingReturn || addingDeliveryMethod}
          disabled={loading || !context?.items?.length || creatingReturn || addingDeliveryMethod || loadingPreview}
          onClick={handleContinue}
        />
      </Styles.LineItemsContainer>
      <NoDeliveryMethodsAvailableModal
        contactEmail={context?.merchantInfo?.contact_email}
        show={showNoDeliveryMethodsAvailableModal}
        onAccept={handleNoDeliveryMethodsAvailable}
        onClose={() => setShowNoDeliveryMethodsAvailableModal(false)}
      />
      <RegionsModal
        show={showRegionModal}
        provinces={getProvinces()}
        selectedCountry={currentCountry?.name}
        selectedProvince={context.orderInfo.shipping_address?.province?.id}
        onAccept={changeRegion}
        onClose={() => setShowRegionModal(false)}
        availableTransactionTypes={context?.orderInfo?.available_transaction_types}
        availableTransactionTypesForRegion={context?.orderInfo?.available_transaction_types_for_region}
        allowsUnifiedFlow={context?.merchantInfo?.preferences?.allows_unified_flow}
      />
      <ExchangeNotAllowedModal show={showExchangeNotAllowedModal} onClose={closeExchangeNotAllowedModal} />
      <ReturnShippedErrorModal show={showReturnShippedErrorModal} onClose={handleCloseShippedErrorModal} />
      <TransactionTypesNotAllowedModal
        show={showTransactionTypesNotAllowedModal}
        onClose={closeTransactionTypesNotAllowedModal}
        getTransactionsNotAllowedText={getTransactionsNotAllowedText}
      />
      <ProductDetailDrawer
        show={showProductDetailDrawer}
        onClose={closeProductDetailDrawer}
        product={selectedProduct}
        context={context}
        setContext={setContext}
        isCancellationsPortal={isCancellationsPortal}
        getItemDefaultTransactionType={getItemDefaultTransactionType}
        showTransactionTypeSelector={showTransactionTypeSelector}
        getReturnReasonsService={services.getReturnReasons}
        uploadProductImageService={services.uploadProductImage}
        getProductsTaxonomyService={services.getProductsTaxonomy}
        getProductsByTaxonomyService={services.getProductsByTaxonomy}
        getRecommendedExchangeProductsService={services.getRecommendedExchangeProducts}
        searchProductsService={services.searchProducts}
        getVariantsByProductService={services.getVariantsByProduct}
        getRecommendedExchangeVariantsService={services.getRecommendedExchangeVariants}
        getEstimatedDeliveryMethodsService={services.getEstimatedDeliveryMethods}
        onTrackEvent={sendEvent}
      />
      <ConsentModal
        show={showConsentModal}
        consentModal={consentModal}
        onConfirm={confirmConsent}
        onClose={closeConsentModal}
      />
    </Loading>
  )
}

export default ProductsSelectionPage
